import React, { useState } from "react";
import { Play, Loader2, Download, AlertCircle, CheckCircle2, Activity, Beaker, Droplets, Thermometer, Zap, Wind, GitBranch, FileSpreadsheet, FileText } from "lucide-react";
import api, { formatErr } from "../lib/api";
import AccuracyCard from "./AccuracyCard";

const TOOLS = [
  { id: "select-model", label: "Auto-Select Thermo Model", icon: GitBranch, group: "thermo" },
  { id: "flash", label: "Flash (PT/PH/PS/TV/PV/Bubble/Dew)", icon: Activity, group: "thermo" },
  { id: "stream", label: "Process Stream", icon: Droplets, group: "thermo" },
  { id: "petroleum", label: "Petroleum Crude (TBP)", icon: Beaker, group: "thermo" },
  { id: "joback", label: "Joback (SMILES → Properties)", icon: Beaker, group: "thermo" },
  { id: "column", label: "Distillation FUG (+Fair Diameter)", icon: Wind, group: "unitops" },
  { id: "reactor", label: "Reactor (Conv / Eq / CSTR)", icon: Beaker, group: "unitops" },
  { id: "heat-exchanger", label: "HX — LMTD", icon: Thermometer, group: "unitops" },
  { id: "heat-exchanger-ntu", label: "HX — Effectiveness-NTU", icon: Thermometer, group: "unitops" },
  { id: "heat-exchanger-kern-dp", label: "HX — Kern Shell ΔP", icon: Thermometer, group: "unitops" },
  { id: "pump", label: "Pump (with NPSHa)", icon: Activity, group: "unitops" },
  { id: "compressor", label: "Compressor (Isen + Poly)", icon: Activity, group: "unitops" },
  { id: "pipe", label: "Pipe — single phase", icon: Droplets, group: "unitops" },
  { id: "pipe-two-phase", label: "Pipe — Beggs-Brill / Lockhart-Martinelli", icon: Droplets, group: "unitops" },
  { id: "psv", label: "PSV (API 520 — Gas / Liquid / DIERS)", icon: AlertCircle, group: "unitops" },
  { id: "control-valve", label: "Control Valve (full ISA 75.01)", icon: Activity, group: "unitops" },
  { id: "terminal-velocity", label: "Particle Terminal Velocity", icon: Wind, group: "unitops" },
  { id: "cyclone", label: "Cyclone (Stairmand)", icon: Wind, group: "unitops" },
  { id: "drag-coefficient", label: "Drag Coefficient (sphere)", icon: Wind, group: "unitops" },
  { id: "coolprop", label: "CoolProp (Pure Fluid)", icon: Thermometer, group: "props" },
  { id: "steam", label: "Steam Tables (IAPWS-IF97)", icon: Wind, group: "props" },
  { id: "humid-air", label: "Humid Air (HAPropsSI)", icon: Wind, group: "props" },
  { id: "utility", label: "Utility Consumption", icon: Zap, group: "props" },
  { id: "mass-balance", label: "Flowsheet Mass Balance", icon: GitBranch, group: "props" },
  { id: "energy-balance", label: "Flowsheet Energy Balance", icon: GitBranch, group: "props" },
];

export default function SimulationV3Panel({ projectId }) {
  const [tool, setTool] = useState("select-model");
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  // Wave Z — auto-fill state
  const [prefillMeta, setPrefillMeta] = useState({ autofilled: false, source: "", overrides: {} });
  const [selectorOpts, setSelectorOpts] = useState({}); // {available_streams, available_pumps, ...}
  const [writebackInfo, setWritebackInfo] = useState(null);

  // Map tool id → calculator id used by the prefill backend.
  const toolToCalc = (t) => ({
    "heat-exchanger-ntu": "heat-exchanger-ntu",
    "heat-exchanger-kern-dp": "heat-exchanger-kern-dp",
  }[t] || t);

  // Fetch prefill whenever the calculator tool changes (and we have a
  // projectId so it's not a generic standalone use).
  React.useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setPrefillMeta({ autofilled: false, source: "", overrides: {} });
    setSelectorOpts({});
    setResult(null);
    setWritebackInfo(null);
    api.get(`/projects/${projectId}/simulation/v3-prefill?calculator=${toolToCalc(tool)}`)
      .then((r) => {
        if (!alive) return;
        const f = r.data.fields || {};
        // Separate selector arrays from input fields
        const selKeys = ["available_streams", "available_pumps", "available_compressors",
                         "available_columns", "available_hx", "available_lines",
                         "selected_stream", "selected_pump", "selected_compressor",
                         "selected_column", "selected_hx", "selected_line",
                         "selected_psv", "selected_control_valve"];
        const selectors = {};
        const fields = {};
        Object.entries(f).forEach(([k, v]) => {
          if (k.startsWith("_")) return;
          if (selKeys.includes(k)) selectors[k] = v;
          else fields[k] = v;
        });
        setSelectorOpts(selectors);
        setInputs(fields);
        setPrefillMeta({
          autofilled: !!f._autofilled,
          source: f._source || "",
          suggested: Array.isArray(f._suggested) ? f._suggested : [],
          overrides: {},
          autoFilledKeys: Object.keys(fields),
          // Wave AB+ — for select-model panel, capture what's already in PDO
          currentThermoModel: f._current_thermo_model,
          currentThermoReasoning: f._current_thermo_reasoning,
          approvedDesignCase: f._approved_design_case,
        });
      })
      .catch(() => { /* ignore — manual entry mode */ });
    return () => { alive = false; };
  }, [tool, projectId]);

  // Setter wrapper that records overrides so we can paint them orange.
  const updateInput = (key, val) => {
    setInputs((o) => ({ ...o, [key]: val }));
    setPrefillMeta((m) => {
      // If the user changes an originally-auto-filled field, mark it.
      if ((m.autoFilledKeys || []).includes(key)) {
        return { ...m, overrides: { ...m.overrides, [key]: true } };
      }
      return m;
    });
  };

  const handleRun = async () => {
    setRunning(true); setErr(""); setResult(null); setWritebackInfo(null);
    try {
      const body = buildRequestBody(tool, inputs);
      const path = pathFor(tool);
      const r = await api.post(`/simulation/v3/${path}`, body);
      setResult(r.data);
      // Write back to PDO if possible
      if (projectId) {
        try {
          const wb = await api.post(`/projects/${projectId}/simulation/v3-writeback`, {
            calculator: toolToCalc(tool),
            inputs,
            results: r.data || {},
          });
          if (wb.data?.applied > 0) {
            setWritebackInfo(`Saved to PDO (${wb.data.applied} section${wb.data.applied > 1 ? 's' : ''} updated)`);
          }
        } catch { /* writeback failure shouldn't fail the calc */ }
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setRunning(false); }
  };

  const handleExport = async (format) => {
    if (!result) return;
    try {
      const r = await api.post(`/simulation/v3/export`, {
        format, payload: result, filename: `rachana_${tool}`,
      }, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const exts = { csv: "csv", xlsx: "xlsx", json: "json", pdf: "pdf", hysys: "csv", staad: "std", ifc: "ifc", dxf: "dxf" };
      a.download = `rachana_${tool}.${exts[format] || format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(`Export failed: ${formatErr(e.response?.data?.detail) || e.message}`);
    }
  };

  const tDef = TOOLS.find((t) => t.id === tool);
  const Icon = tDef?.icon || Activity;

  return (
    <div data-testid="sim-v3-panel" style={{ padding: 14, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ marginBottom: 12 }}>
        <div className="pf-overline" style={{ color: "var(--pf-accent)" }}>CAPEXARA SIMULATOR · ENGINE v3.0</div>
        <h2 style={{ fontSize: 18, margin: "4px 0", color: "var(--pf-text)" }}>Process Plant Simulation Suite</h2>
        <div className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-dim)" }}>
          Powered by chemicals · thermo · fluids · CoolProp · 20,000+ components · NIST-validated EOS · API 520 · ISA 75.01
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr 380px", gap: 12, flex: 1, overflow: "hidden" }}>
        {/* Left rail — tool list */}
        <div style={{ overflowY: "auto", border: "1px solid var(--pf-border)", padding: 6, background: "var(--pf-surface)" }}>
          {["thermo", "unitops", "props"].map((grp) => (
            <div key={grp} style={{ marginBottom: 12 }}>
              <div className="pf-overline" style={{ fontSize: 9, color: "var(--pf-text-muted)", padding: "4px 6px" }}>
                {grp === "thermo" ? "THERMODYNAMICS" : grp === "unitops" ? "UNIT OPERATIONS" : "PROPERTIES & UTILITIES"}
              </div>
              {TOOLS.filter((t) => t.group === grp).map((t) => {
                const I = t.icon;
                const active = tool === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`sim-v3-tool-${t.id}`}
                    onClick={() => { setTool(t.id); setInputs({}); setResult(null); setErr(""); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "6px 8px",
                      width: "100%", border: "1px solid " + (active ? "var(--pf-accent)" : "transparent"),
                      background: active ? "rgba(255,176,0,0.10)" : "transparent",
                      color: active ? "var(--pf-accent)" : "var(--pf-text)",
                      fontSize: 12, cursor: "pointer", textAlign: "left", marginBottom: 2,
                    }}
                  >
                    <I size={12}/>{t.label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Center — input form + result */}
        <div style={{ overflowY: "auto", border: "1px solid var(--pf-border)", padding: 14, background: "var(--pf-surface)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Icon size={18} color="#FFB000"/>
            <h3 style={{ margin: 0, fontSize: 15, color: "var(--pf-text)" }}>{tDef?.label}</h3>
          </div>

          {/* Wave Z — auto-fill banner */}
          {prefillMeta.autofilled && projectId && (
            <div data-testid="v3-prefill-banner" style={{
              padding: "6px 10px", marginBottom: 10, borderRadius: 4,
              background: "rgba(59,130,246,0.10)", border: "1px solid #3b82f6",
              color: "#7dd3fc", fontSize: 11, display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ fontSize: 13 }}>🔗</span>
              Auto-filled from {prefillMeta.source}. Edit any field to override.
            </div>
          )}
          {/* Wave AB — manual-only banner for calculators that the spec
              explicitly defines as "Manual entry only" (Joback, CoolProp,
              Drag coefficient). Spec Part 3.1 §1, §19, §20. */}
          {projectId && !prefillMeta.autofilled && ["joback", "coolprop", "drag-coefficient"].includes(tool) && (
            <div data-testid="v3-prefill-manual" style={{
              padding: "6px 10px", marginBottom: 10, borderRadius: 4,
              background: "rgba(168,85,247,0.10)", border: "1px solid #a855f7",
              color: "#d8b4fe", fontSize: 11, display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ fontSize: 13 }}>✏</span>
              Manual entry only — this calculator works on {
                tool === "joback" ? "chemical structure (SMILES)" :
                tool === "coolprop" ? "pure-fluid lookup (no PDO read needed)" :
                "first-principles inputs"
              }.
            </div>
          )}
          {projectId && !prefillMeta.autofilled && !["joback", "coolprop", "drag-coefficient"].includes(tool) && (
            <div data-testid="v3-prefill-empty" style={{
              padding: "6px 10px", marginBottom: 10, borderRadius: 4,
              background: "transparent", border: "1px dashed var(--pf-border)",
              color: "var(--pf-text-muted)", fontSize: 11,
            }}>
              No PDO data available — enter values manually, or run Project Auto-Sim first.
            </div>
          )}

          {/* Wave Z — selector dropdowns (stream / equipment / line) */}
          <SelectorRow
            opts={selectorOpts}
            onSelect={async (key, value) => {
              setSelectorOpts((o) => ({ ...o, [key]: value }));
              // Re-fetch prefill with selected entity hint via a query param
              if (!projectId) return;
              const ent = String(key).replace(/^selected_/, "");
              try {
                const r = await api.get(
                  `/projects/${projectId}/simulation/v3-prefill?calculator=${toolToCalc(tool)}&select_${ent}=${encodeURIComponent(value)}`
                );
                const f = r.data.fields || {};
                const next = {};
                Object.entries(f).forEach(([k, v]) => {
                  if (k.startsWith("_")) return;
                  if (k.startsWith("available_") || k.startsWith("selected_")) return;
                  next[k] = v;
                });
                setInputs((cur) => ({ ...cur, ...next }));
              } catch { /* ignore */ }
            }}
          />

          {renderFormInGrid(tool, inputs, updateInput, prefillMeta)}

          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button
              data-testid="sim-v3-run"
              className="btn btn-primary"
              disabled={running}
              onClick={handleRun}
              style={{ padding: "8px 16px" }}
            >
              {running ? <><Loader2 className="pf-spin" size={14}/> Calculating…</> : <><Play size={14}/> Calculate</>}
            </button>
            {result && (
              <div className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-dim)", display: "flex", alignItems: "center", gap: 4 }}>
                <CheckCircle2 size={12} color="#4FC3F7"/> Result ready
              </div>
            )}
            {writebackInfo && (
              <div data-testid="v3-writeback-info" className="pf-mono" style={{ fontSize: 11, color: "#86efac", display: "flex", alignItems: "center", gap: 4 }}>
                <CheckCircle2 size={12} color="#22c55e"/> {writebackInfo}
              </div>
            )}
          </div>

          {err && (
            <div className="pf-sev pf-sev-error" data-testid="sim-v3-error" style={{ marginTop: 10, padding: "6px 10px", display: "flex", alignItems: "center", gap: 6 }}>
              <AlertCircle size={13}/> {err}
            </div>
          )}

          {/* Wave AB+ — "Used by Auto-Sim" badge for the select-model panel */}
          {tool === "select-model" && projectId && prefillMeta.currentThermoModel && (
            <div data-testid="v3-current-thermo-badge" style={{
              padding: "8px 12px", marginBottom: 10, borderRadius: 4,
              background: "rgba(34,197,94,0.10)", border: "1px solid #22c55e",
              color: "#86efac", fontSize: 11, display: "flex", alignItems: "center", gap: 8,
              flexWrap: "wrap",
            }}>
              <CheckCircle2 size={13} />
              <span>
                <b>Used by Auto-Sim:</b> <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{prefillMeta.currentThermoModel}</span>
              </span>
              {prefillMeta.currentThermoReasoning && (
                <span style={{ color: "var(--pf-text-muted)", fontSize: 10.5 }}>
                  · {prefillMeta.currentThermoReasoning}
                </span>
              )}
            </div>
          )}

          {result && (
            <div style={{ marginTop: 16 }} data-testid="sim-v3-result">
              <div className="pf-overline" style={{ fontSize: 10, color: "var(--pf-accent)", marginBottom: 6 }}>RESULT</div>
              <ResultTable result={result} />

              {/* Wave AB+ — Re-run Auto-Sim with new model when select-model
                  recommendation differs from what's persisted in PDO. */}
              {tool === "select-model" && projectId && result?.model
                && result.model !== prefillMeta.currentThermoModel
                && prefillMeta.approvedDesignCase && (
                <div data-testid="v3-rerun-autosim-banner" style={{
                  padding: "10px 14px", marginTop: 12, borderRadius: 4,
                  background: "rgba(255,176,0,0.10)", border: "1px solid #FFB000",
                  color: "#fde68a", fontSize: 12,
                }}>
                  <div style={{ marginBottom: 6 }}>
                    Recommendation <b>{result.model}</b> differs from the model
                    currently used by Auto-Sim
                    {prefillMeta.currentThermoModel ? <> (<b>{prefillMeta.currentThermoModel}</b>)</> : null}.
                  </div>
                  <button
                    type="button"
                    data-testid="v3-rerun-autosim-btn"
                    onClick={async () => {
                      try {
                        // Writeback already saved on calc run; this triggers
                        // re-run of the approved design case.
                        const designId = prefillMeta.approvedDesignCase;
                        if (!designId) return;
                        await api.post(`/projects/${projectId}/simulation/cases/${designId}/run`);
                        setWritebackInfo(`Auto-Sim re-run with ${result.model} — refresh Project Auto-Sim to see new results`);
                      } catch (e) {
                        setErr(formatErr(e.response?.data?.detail) || e.message);
                      }
                    }}
                    style={{
                      padding: "5px 10px", fontSize: 11,
                      background: "rgba(255,176,0,0.20)", border: "1px solid #FFB000",
                      color: "#fde68a", borderRadius: 3, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 5,
                    }}
                  >
                    <Play size={11} /> Re-run Auto-Sim with {result.model}
                  </button>
                </div>
              )}

              <div className="pf-overline" style={{ fontSize: 10, color: "var(--pf-accent)", marginTop: 16, marginBottom: 6 }}>EXPORT</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <ExportBtn label="JSON" icon={FileText} onClick={() => handleExport("json")} testid="sim-v3-export-json"/>
                <ExportBtn label="CSV" icon={FileSpreadsheet} onClick={() => handleExport("csv")} testid="sim-v3-export-csv"/>
                <ExportBtn label="Excel" icon={FileSpreadsheet} onClick={() => handleExport("xlsx")} testid="sim-v3-export-xlsx"/>
                <ExportBtn label="PDF" icon={FileText} onClick={() => handleExport("pdf")} testid="sim-v3-export-pdf"/>
                {tool === "stream" && <ExportBtn label="HYSYS" icon={Download} onClick={() => handleExport("hysys")} testid="sim-v3-export-hysys"/>}
              </div>
            </div>
          )}
        </div>

        {/* Right rail — accuracy card */}
        <div style={{ overflowY: "auto" }}>
          {result?.accuracy_report
            ? <AccuracyCard report={result.accuracy_report} />
            : <Placeholder>Run a calculation to see the accuracy report.</Placeholder>}
        </div>
      </div>
    </div>
  );
}

// ────────── Sub-components ──────────
function SelectorRow({ opts, onSelect }) {
  const groups = [
    { entityKey: "stream", optsKey: "available_streams", selKey: "selected_stream", label: "Stream", labelKey: "id" },
    { entityKey: "pump", optsKey: "available_pumps", selKey: "selected_pump", label: "Pump", labelKey: "tag" },
    { entityKey: "compressor", optsKey: "available_compressors", selKey: "selected_compressor", label: "Compressor", labelKey: "tag" },
    { entityKey: "column", optsKey: "available_columns", selKey: "selected_column", label: "Column", labelKey: "tag" },
    { entityKey: "hx", optsKey: "available_hx", selKey: "selected_hx", label: "Heat exchanger", labelKey: "tag" },
    { entityKey: "line", optsKey: "available_lines", selKey: "selected_line", label: "Line", labelKey: "line_number" },
    { entityKey: "psv", optsKey: "available_psvs", selKey: "selected_psv", label: "PSV", labelKey: "tag" },
    { entityKey: "control_valve", optsKey: "available_control_valves", selKey: "selected_control_valve", label: "Control Valve", labelKey: "tag" },
  ];
  const visible = groups.filter((g) => Array.isArray(opts[g.optsKey]) && opts[g.optsKey].length > 0);
  if (!visible.length) return null;
  return (
    <div data-testid="v3-selector-row" style={{
      display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10,
      padding: "8px 10px", background: "var(--pf-surface-2)",
      border: "1px solid var(--pf-border)", borderRadius: 4,
    }}>
      {visible.map((g) => {
        const list = opts[g.optsKey] || [];
        const cur = opts[g.selKey] ?? list[0]?.[g.labelKey];
        return (
          <label key={g.optsKey} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--pf-text-muted)" }}>
            <span className="pf-overline">{g.label}</span>
            <select
              data-testid={`v3-select-${g.entityKey}`}
              value={cur ?? ""}
              onChange={(e) => onSelect(g.selKey, e.target.value)}
              className="pf-mono"
              style={{
                background: "var(--pf-surface)", color: "var(--pf-text)",
                border: "1px solid var(--pf-border)", padding: "3px 6px",
                fontSize: 11, borderRadius: 3,
              }}
            >
              {list.map((it) => (
                <option key={it[g.labelKey]} value={it[g.labelKey]}>{it.label || it[g.labelKey]}</option>
              ))}
            </select>
          </label>
        );
      })}
    </div>
  );
}

function ExportBtn({ label, icon: I, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="btn btn-ghost"
      style={{ padding: "5px 10px", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}
    >
      <I size={12}/> {label}
    </button>
  );
}

function Placeholder({ children }) {
  return (
    <div style={{ border: "1px dashed var(--pf-border)", padding: 14, textAlign: "center", color: "var(--pf-text-dim)", fontSize: 12 }}>
      {children}
    </div>
  );
}

function ResultTable({ result }) {
  const flat = flatten(result, "", 2);
  // Don't show accuracy_report keys here (rendered in sidebar).
  const rows = Object.entries(flat).filter(([k]) => !k.startsWith("accuracy_report"));
  return (
    <div style={{ border: "1px solid var(--pf-border)", maxHeight: 340, overflowY: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderBottom: "1px solid var(--pf-border-muted)" }}>
              <td className="pf-mono" style={{ padding: "4px 8px", color: "var(--pf-text-muted)", whiteSpace: "nowrap" }}>{k}</td>
              <td className="pf-mono" style={{ padding: "4px 8px", color: "var(--pf-text)", textAlign: "right" }}>{fmt(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function flatten(o, p, depth) {
  const out = {};
  if (depth < 0 || o === null || typeof o !== "object" || Array.isArray(o)) {
    if (p) out[p] = o;
    return out;
  }
  for (const [k, v] of Object.entries(o)) {
    const key = p ? `${p}.${k}` : k;
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(out, flatten(v, key, depth - 1));
    } else if (Array.isArray(v) && v.length && typeof v[0] === "object") {
      out[key] = `[${v.length} rows]`;
    } else {
      out[key] = v;
    }
  }
  return out;
}

function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") {
    if (Math.abs(v) >= 100000 || (v !== 0 && Math.abs(v) < 0.001)) return v.toExponential(3);
    return Number.isInteger(v) ? v : Number(v.toFixed(4));
  }
  if (Array.isArray(v)) return v.slice(0, 12).join(", ") + (v.length > 12 ? "…" : "");
  return String(v);
}

// ────────── Input form switcher ──────────
function renderFormInGrid(tool, inputs, updateInput, prefillMeta) {
  const content = renderInputForm(tool, inputs, updateInput, prefillMeta);
  if (!content) return null;
  // Single-input tools render flush; multi-field tools use responsive grid.
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
      gap: 10,
    }}>{content}</div>
  );
}

function renderInputForm(tool, inputs, updateInput, prefillMeta) {
  const meta = prefillMeta || { autoFilledKeys: [], suggested: [], overrides: {} };
  const isAutoFilled = (k) => (meta.autoFilledKeys || []).includes(k) && !(meta.overrides || {})[k];
  const isSuggested = (k) => (meta.suggested || []).includes(k);
  const isOverridden = (k) => !!(meta.overrides || {})[k];
  const fieldStyle = (k) => {
    if (isOverridden(k)) {
      return { borderLeft: "3px solid #f97316", background: "rgba(249,115,22,0.05)" };
    }
    if (isAutoFilled(k)) {
      return { borderLeft: "3px solid #3b82f6", background: "rgba(59,130,246,0.05)" };
    }
    if (isSuggested(k)) {
      return { borderLeft: "3px solid #8b5cf6", background: "rgba(139,92,246,0.05)" };
    }
    return {};
  };
  const fieldIcon = (k) => {
    if (isOverridden(k)) return "✎";
    if (isAutoFilled(k)) return "🔗";
    if (isSuggested(k)) return "✨";
    return null;
  };
  const fieldTitle = (k) => {
    if (isOverridden(k)) return "Manually overridden";
    if (isAutoFilled(k)) return "Auto-filled from PDO. Edit to override.";
    if (isSuggested(k)) return "AI suggestion — edit to change.";
    return undefined;
  };
  const I = (label, key, type = "number", options) => (
    <label key={key} style={{ display: "block", marginBottom: 0 }} title={fieldTitle(key)}>
      <span className="pf-mono" style={{
        fontSize: 10, color: "var(--pf-text-muted)", display: "flex",
        gap: 4, alignItems: "center", letterSpacing: 1, marginBottom: 3,
      }}>
        {label}
        {fieldIcon(key) && <span style={{ fontSize: 10, opacity: 0.85 }}>{fieldIcon(key)}</span>}
      </span>
      {options ? (
        <select
          className="pf-input pf-mono"
          data-testid={`sim-v3-in-${key}`}
          value={inputs[key] ?? options[0]}
          onChange={(e) => updateInput(key, e.target.value)}
          style={{ width: "100%", ...fieldStyle(key) }}
        >
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          className="pf-input pf-mono"
          data-testid={`sim-v3-in-${key}`}
          name={key}
          type={type}
          value={inputs[key] ?? ""}
          onChange={(e) => updateInput(key, e.target.value)}
          style={{ width: "100%", ...fieldStyle(key) }}
        />
      )}
    </label>
  );

  if (tool === "select-model") {
    return I("Components (comma-separated, e.g. methanol, water, ethanol)", "components", "text");
  }
  if (tool === "flash") {
    return (
      <>
        {I("Components", "components", "text")}
        {I("EOS / Activity Model", "model", "text", ["PR", "SRK", "NRTL", "UNIFAC", "IDEAL"])}
        {I("Mole Fractions zs (comma-separated, sum=1)", "zs", "text")}
        {I("Flash Type", "flash_type", "text", ["PT", "PH", "PS", "TV", "PV", "Bubble (VF=0)", "Dew (VF=1)"])}
        {I("T (K)", "T_K")} {I("P (Pa)", "P_Pa")}
        {I("H (J/mol)  — for PH only", "H_Jmol")} {I("S (J/mol/K) — for PS only", "S_JmolK")}
        {I("VF (0..1) — for TV / PV", "VF")}
      </>
    );
  }
  if (tool === "stream") {
    return (
      <>
        {I("Components", "components", "text")}
        {I("Composition (e.g. ethane:30, propane:40, butane:30)", "composition", "text")}
        {I("Model", "model", "text", ["PR", "SRK", "NRTL"])}
        {I("T (°C)", "T_C")} {I("P (barg)", "P_barg")} {I("Mass flow (kg/h)", "mass_flow_kgh")}
      </>
    );
  }
  if (tool === "petroleum") {
    return (
      <>
        {I("Crude name", "crude_name", "text")}
        {I("TBP volume %  (e.g. 0,5,10,30,50,70,90,100)", "tbp_volume_pct", "text")}
        {I("TBP temperatures °C (matching above)", "tbp_temperatures_C", "text")}
        {I("API gravity", "api_gravity")}
        {I("Number of pseudo-components", "num_pseudocomponents")}
      </>
    );
  }
  if (tool === "column") {
    return (
      <>
        {I("Components", "components", "text")}
        {I("Composition", "composition", "text")}
        {I("Light key", "light_key", "text")}
        {I("Heavy key", "heavy_key", "text")}
        {I("LK recovery (0-1)", "recovery_LK")}
        {I("HK recovery in distillate (0-1)", "recovery_HK")}
        {I("Reflux ratio factor (R/Rmin)", "reflux_ratio_factor")}
        {I("T (°C)", "T_C")} {I("P (barg)", "P_barg")} {I("Feed mass flow kg/h", "mass_flow_kgh")}
      </>
    );
  }
  if (tool === "reactor") {
    return (
      <>
        {I("Components", "components", "text")}
        {I("Composition", "composition", "text")}
        {I("Reactor kind", "kind", "text", ["conversion", "equilibrium", "cstr"])}
        {I("Reactions JSON", "reactions", "text")}
        {I("T reactor (°C)", "T_reactor_C")}
        {I("Volume m³ (CSTR only)", "volume_m3")}
        {I("Feed T (°C)", "T_C")} {I("P barg", "P_barg")} {I("Feed kg/h", "mass_flow_kgh")}
      </>
    );
  }
  if (tool === "heat-exchanger") {
    return (
      <>
        {I("Hot inlet T (K)", "hot_in_C")} {I("Hot outlet T (K)", "hot_out_C")}
        {I("Cold inlet T (K)", "cold_in_C")} {I("Cold outlet T (K)", "cold_out_C")}
        {I("Duty Q (kW)", "Q_kW")} {I("U (W/m²K)", "U_Wm2K")} {I("F factor", "F")}
        {I("Flow", "flow_arrangement", "text", ["counter", "parallel"])}
      </>
    );
  }
  if (tool === "pump") {
    return (
      <>
        {I("Q (m³/h)", "Q_m3h")} {I("ΔP (bar)", "dP_bar")}
        {I("Density (kg/m³)", "fluid_density_kgm3")}
        {I("Hydraulic efficiency (0-1)", "efficiency")}
        {I("Motor efficiency (0-1)", "motor_efficiency")}
      </>
    );
  }
  if (tool === "pipe") {
    return (
      <>
        {I("Diameter (m)", "diameter_m")} {I("Length (m)", "length_m")}
        {I("Mass flow (kg/s)", "mass_flow_kgs")}
        {I("Density (kg/m³)", "density_kgm3")}
        {I("Viscosity (Pa·s)", "viscosity_Pas")}
        {I("Roughness (m)", "roughness_m")}
      </>
    );
  }
  if (tool === "psv") {
    return (
      <>
        {I("Mass flow (kg/s)", "mass_flow_kgs")}
        {I("P1 inlet (Pa)", "P1_Pa")} {I("P2 outlet (Pa)", "P2_Pa")}
        {I("T (K)", "T_K")} {I("MW", "MW")} {I("k = Cp/Cv", "k_ratio")}
        {I("Z compressibility", "Z")} {I("Service", "service", "text", ["vapor", "liquid", "two_phase"])}
        {I("Density (kg/m³) — liquid/two_phase", "density_kgm3")}
        {I("Pc (Pa) — two_phase", "Pc_Pa")}
        {I("Psat (Pa) — two_phase", "Psat_Pa")}
      </>
    );
  }
  if (tool === "control-valve") {
    return (
      <>
        {I("Mass flow (kg/s)", "mass_flow_kgs")}
        {I("P1 (Pa)", "P1_Pa")} {I("P2 (Pa)", "P2_Pa")}
        {I("Density (kg/m³)", "density_kgm3")}
        {I("T (K)", "T_K")} {I("Service", "service", "text", ["liquid", "gas"])}
        {I("Psat (Pa) — liquid", "Psat_Pa")}
        {I("Pc (Pa) — liquid", "Pc_Pa")}
        {I("Viscosity (Pa·s)", "mu_Pas")}
        {I("MW — gas", "MW")} {I("γ = Cp/Cv — gas", "gamma")}
      </>
    );
  }
  if (tool === "compressor") {
    return (
      <>
        {I("Mass flow (kg/s)", "mass_flow_kgs")}
        {I("T inlet (K)", "T1_K")}
        {I("P1 (Pa)", "P1_Pa")} {I("P2 (Pa)", "P2_Pa")}
        {I("k = Cp/Cv", "k_ratio")} {I("Z", "Z")}
        {I("MW", "MW")}
        {I("Isentropic η", "eta_isen")}
        {I("Polytropic η (optional)", "eta_poly")}
        {I("Motor η", "eta_motor")}
      </>
    );
  }
  if (tool === "heat-exchanger-ntu") {
    return (
      <>
        {I("Hot mass flow (kg/s)", "mh_kgs")} {I("Cold mass flow (kg/s)", "mc_kgs")}
        {I("Hot Cp (J/kg/K)", "Cph_JkgK")} {I("Cold Cp (J/kg/K)", "Cpc_JkgK")}
        {I("T hot inlet (°C)", "T_hot_in_C")} {I("T cold inlet (°C)", "T_cold_in_C")}
        {I("UA (W/K)", "UA_WK")}
        {I("Subtype", "subtype", "text", ["counterflow", "parallel", "crossflow", "TEMA E", "TEMA J", "TEMA G", "TEMA H", "plate"])}
        {I("# shell-tube passes (optional)", "n_shell_tube")}
      </>
    );
  }
  if (tool === "heat-exchanger-kern-dp") {
    return (
      <>
        {I("Mass flow shell-side (kg/s)", "m_kgs")}
        {I("Density (kg/m³)", "rho_kgm3")} {I("Viscosity (Pa·s)", "mu_Pas")}
        {I("Shell ID (m)", "D_shell_m")} {I("Baffle spacing (m)", "baffle_spacing_m")}
        {I("Tube pitch (m)", "tube_pitch_m")} {I("Tube OD (m)", "tube_OD_m")}
        {I("# baffles", "n_baffles")}
      </>
    );
  }
  if (tool === "pipe-two-phase") {
    return (
      <>
        {I("Diameter (m)", "diameter_m")} {I("Length (m)", "length_m")}
        {I("Mass flow total (kg/s)", "m_total_kgs")}
        {I("Vapor quality x (0-1)", "vapor_quality")}
        {I("ρ_l (kg/m³)", "rho_l_kgm3")} {I("ρ_g (kg/m³)", "rho_g_kgm3")}
        {I("μ_l (Pa·s)", "mu_l_Pas")} {I("μ_g (Pa·s)", "mu_g_Pas")}
        {I("σ surface tension (N/m)", "sigma_Nm")}
        {I("P (Pa)", "P_Pa")}
        {I("Inclination (°)", "angle_deg")}
        {I("Roughness (m)", "roughness_m")}
        {I("Method", "method", "text", ["Beggs_Brill", "Lockhart_Martinelli"])}
      </>
    );
  }
  if (tool === "terminal-velocity") {
    return (
      <>
        {I("Particle diameter (m)", "particle_D_m")}
        {I("Particle density (kg/m³)", "particle_density_kgm3")}
        {I("Fluid density (kg/m³)", "fluid_density_kgm3")}
        {I("Fluid viscosity (Pa·s)", "fluid_viscosity_Pas")}
      </>
    );
  }
  if (tool === "cyclone") {
    return (
      <>
        {I("Gas flow (m³/s)", "gas_flow_m3s")}
        {I("Gas density (kg/m³)", "gas_density_kgm3")}
        {I("Gas viscosity (Pa·s)", "gas_viscosity_Pas")}
        {I("Particle density (kg/m³)", "particle_density_kgm3")}
        {I("Design", "design", "text", ["high_efficiency", "high_throughput"])}
      </>
    );
  }
  if (tool === "drag-coefficient") {
    return (<>{I("Reynolds number", "Re")} {I("Sphericity (0-1)", "sphericity")}</>);
  }
  if (tool === "humid-air") {
    return (
      <>
        {I("T dry-bulb (°C)", "T_dry_C")}
        {I("Pressure (bar)", "P_bar")}
        {I("RH (0-1) — pick ONE", "RH")}
        {I("T wet-bulb (°C) — alt", "T_wet_C")}
        {I("T dew-point (°C) — alt", "T_dew_C")}
        {I("Humidity ratio W (kg/kg) — alt", "W_kgkg")}
      </>
    );
  }
  if (tool === "joback") {
    return (
      <>
        {I("SMILES (e.g. CCO, c1ccccc1, CCCCCC)", "smiles", "text")}
        {I("T (K) — for Cp", "T_K")}
      </>
    );
  }
  if (tool === "mass-balance") {
    return (
      <>
        {I("Streams JSON (e.g. {\"F1\":{\"molar_flow\":100,\"MW\":18},\"P1\":{\"molar_flow\":99.5,\"MW\":18}})", "streams_json", "text")}
        {I("Feed stream IDs (comma)", "feed_streams", "text")}
        {I("Product stream IDs (comma)", "product_streams", "text")}
        {I("Tolerance %", "tolerance_pct")}
      </>
    );
  }
  if (tool === "energy-balance") {
    return (
      <>
        {I("Streams JSON (use H_kW field per stream)", "streams_json", "text")}
        {I("Feed stream IDs (comma)", "feed_streams", "text")}
        {I("Product stream IDs (comma)", "product_streams", "text")}
        {I("Equipment duties kW (comma — heat IN positive)", "equipment_duties_kW", "text")}
        {I("Tolerance %", "tolerance_pct")}
      </>
    );
  }
  if (tool === "coolprop") {
    return (
      <>
        {I("Fluid", "fluid", "text", ["Water", "R134a", "R404A", "R744", "R717", "Methane", "Nitrogen", "Oxygen", "Propane", "Ethane"])}
        {I("T (°C)", "T_C")} {I("P (bar)", "P_bar")}
      </>
    );
  }
  if (tool === "steam") {
    return (
      <>
        {I("T (°C)", "T_C")} {I("P (bar)", "P_bar")}
        {I("Enthalpy (kJ/kg)", "enthalpy_kJkg")}
        {I("Quality 0..1 (sat=Q)", "quality")}
      </>
    );
  }
  if (tool === "utility") {
    return (
      <>
        {I("Duty (kW)", "duty_kW")}
        {I("Utility type", "utility_type", "text", ["steam_HP", "steam_MP", "steam_LP", "cooling_water", "electricity", "refrigerant", "natural_gas"])}
        {I("CW inlet T (°C)", "cw_inlet_T_C")}
        {I("CW outlet T (°C)", "cw_outlet_T_C")}
        {I("Hours per year", "hours_per_year")}
      </>
    );
  }
  return null;
}

// ────────── Build request body ──────────
function pathFor(tool) {
  const map = {
    "select-model": "select-model",
    "flash": "flash",
    "stream": "stream",
    "petroleum": "petroleum/characterize",
    "joback": "joback",
    "column": "column/shortcut",
    "reactor": "reactor",
    "heat-exchanger": "unit-op/heat-exchanger",
    "heat-exchanger-ntu": "unit-op/heat-exchanger-ntu",
    "heat-exchanger-kern-dp": "unit-op/heat-exchanger-kern-dp",
    "pump": "unit-op/pump",
    "compressor": "unit-op/compressor",
    "pipe": "unit-op/pipe",
    "pipe-two-phase": "unit-op/pipe-two-phase",
    "psv": "unit-op/psv",
    "control-valve": "unit-op/control-valve",
    "terminal-velocity": "unit-op/terminal-velocity",
    "cyclone": "unit-op/cyclone",
    "drag-coefficient": "unit-op/drag-coefficient",
    "coolprop": "coolprop/properties",
    "steam": "steam",
    "humid-air": "humid-air",
    "utility": "utility",
    "mass-balance": "flowsheet/mass-balance",
    "energy-balance": "flowsheet/energy-balance",
  };
  return map[tool] || tool;
}

function buildRequestBody(tool, inputs) {
  const num = (k) => (inputs[k] !== "" && inputs[k] !== undefined && inputs[k] !== null) ? Number(inputs[k]) : undefined;
  const csList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  const compMap = (s) => Object.fromEntries(csList(s).map((p) => {
    const [k, v] = p.split(":").map((x) => x.trim());
    return [k, Number(v) || 0];
  }));

  if (tool === "select-model") return { components: csList(inputs.components) };
  if (tool === "flash") {
    const ft = inputs.flash_type || "PT";
    const out = {
      components: csList(inputs.components),
      model: inputs.model || "PR",
      zs: csList(inputs.zs).map(Number),
    };
    if (ft.startsWith("PT")) { out.T_K = num("T_K"); out.P_Pa = num("P_Pa"); }
    if (ft.startsWith("PH")) { out.P_Pa = num("P_Pa"); out.H_Jmol = num("H_Jmol"); }
    if (ft.startsWith("PS")) { out.P_Pa = num("P_Pa"); out.S_JmolK = num("S_JmolK"); }
    if (ft.startsWith("Bubble")) { out.VF = 0; if (inputs.T_K) out.T_K = num("T_K"); else out.P_Pa = num("P_Pa"); }
    if (ft.startsWith("Dew"))    { out.VF = 1; if (inputs.T_K) out.T_K = num("T_K"); else out.P_Pa = num("P_Pa"); }
    if (ft === "TV") { out.T_K = num("T_K"); out.VF = num("VF"); }
    if (ft === "PV") { out.P_Pa = num("P_Pa"); out.VF = num("VF"); }
    return out;
  }
  if (tool === "stream") {
    return {
      components: csList(inputs.components), model: inputs.model || "PR",
      T_C: num("T_C"), P_barg: num("P_barg"),
      mass_flow_kgh: num("mass_flow_kgh"),
      composition_mol_pct: compMap(inputs.composition),
    };
  }
  if (tool === "petroleum") {
    return {
      crude_name: inputs.crude_name || "Crude",
      tbp_volume_pct: csList(inputs.tbp_volume_pct).map(Number),
      tbp_temperatures_C: csList(inputs.tbp_temperatures_C).map(Number),
      api_gravity: num("api_gravity"),
      num_pseudocomponents: num("num_pseudocomponents") || 10,
    };
  }
  if (tool === "column") {
    return {
      feed: {
        components: csList(inputs.components), model: "PR",
        T_C: num("T_C") ?? 80, P_barg: num("P_barg") ?? 5,
        mass_flow_kgh: num("mass_flow_kgh") ?? 10000,
        composition_mol_pct: compMap(inputs.composition),
      },
      light_key: inputs.light_key, heavy_key: inputs.heavy_key,
      recovery_LK: num("recovery_LK") ?? 0.99,
      recovery_HK: num("recovery_HK") ?? 0.01,
      reflux_ratio_factor: num("reflux_ratio_factor") ?? 1.5,
    };
  }
  if (tool === "reactor") {
    let reactions = [];
    try { reactions = JSON.parse(inputs.reactions || "[]"); } catch {}
    return {
      feed: {
        components: csList(inputs.components), model: "PR",
        T_C: num("T_C") ?? 80, P_barg: num("P_barg") ?? 5,
        mass_flow_kgh: num("mass_flow_kgh") ?? 10000,
        composition_mol_pct: compMap(inputs.composition),
      },
      kind: inputs.kind || "conversion",
      reactions,
      T_reactor_C: num("T_reactor_C"),
      volume_m3: num("volume_m3"),
    };
  }
  if (tool === "heat-exchanger") {
    return {
      hot_in_C: num("hot_in_C"), hot_out_C: num("hot_out_C"),
      cold_in_C: num("cold_in_C"), cold_out_C: num("cold_out_C"),
      Q_kW: num("Q_kW"), U_Wm2K: num("U_Wm2K") ?? 500,
      F: num("F") ?? 0.95, flow_arrangement: inputs.flow_arrangement || "counter",
    };
  }
  if (tool === "pump") {
    return {
      Q_m3h: num("Q_m3h"), dP_bar: num("dP_bar"),
      fluid_density_kgm3: num("fluid_density_kgm3") ?? 1000,
      efficiency: num("efficiency") ?? 0.75,
      motor_efficiency: num("motor_efficiency") ?? 0.92,
      P_suction_bara: num("P_suction_bara"),
      P_vapor_bara: num("P_vapor_bara"),
      static_head_suction_m: num("static_head_suction_m") ?? 0,
      friction_loss_suction_m: num("friction_loss_suction_m") ?? 0,
    };
  }
  if (tool === "pipe") {
    return {
      diameter_m: num("diameter_m"), length_m: num("length_m"),
      mass_flow_kgs: num("mass_flow_kgs"),
      density_kgm3: num("density_kgm3"),
      viscosity_Pas: num("viscosity_Pas"),
      roughness_m: num("roughness_m") ?? 0.000045,
    };
  }
  if (tool === "compressor") {
    return {
      mass_flow_kgs: num("mass_flow_kgs"),
      T1_K: num("T1_K"), P1_Pa: num("P1_Pa"), P2_Pa: num("P2_Pa"),
      k_ratio: num("k_ratio") ?? 1.4, Z: num("Z") ?? 1,
      MW: num("MW") ?? 28.96,
      eta_isen: num("eta_isen") ?? 0.78,
      eta_poly: num("eta_poly"),
      eta_motor: num("eta_motor") ?? 0.95,
    };
  }
  if (tool === "heat-exchanger-ntu") {
    return {
      mh_kgs: num("mh_kgs"), mc_kgs: num("mc_kgs"),
      Cph_JkgK: num("Cph_JkgK"), Cpc_JkgK: num("Cpc_JkgK"),
      T_hot_in_C: num("T_hot_in_C"), T_cold_in_C: num("T_cold_in_C"),
      UA_WK: num("UA_WK"),
      subtype: inputs.subtype || "counterflow",
      n_shell_tube: num("n_shell_tube"),
    };
  }
  if (tool === "heat-exchanger-kern-dp") {
    return {
      m_kgs: num("m_kgs"), rho_kgm3: num("rho_kgm3"), mu_Pas: num("mu_Pas"),
      D_shell_m: num("D_shell_m"), baffle_spacing_m: num("baffle_spacing_m"),
      tube_pitch_m: num("tube_pitch_m"), tube_OD_m: num("tube_OD_m"),
      n_baffles: num("n_baffles"),
    };
  }
  if (tool === "pipe-two-phase") {
    return {
      diameter_m: num("diameter_m"), length_m: num("length_m"),
      m_total_kgs: num("m_total_kgs"), vapor_quality: num("vapor_quality"),
      rho_l_kgm3: num("rho_l_kgm3"), rho_g_kgm3: num("rho_g_kgm3"),
      mu_l_Pas: num("mu_l_Pas"), mu_g_Pas: num("mu_g_Pas"),
      sigma_Nm: num("sigma_Nm") ?? 0.072, P_Pa: num("P_Pa") ?? 101325,
      angle_deg: num("angle_deg") ?? 0,
      roughness_m: num("roughness_m") ?? 0.000045,
      method: inputs.method || "Beggs_Brill",
    };
  }
  if (tool === "terminal-velocity") {
    return {
      particle_D_m: num("particle_D_m"),
      particle_density_kgm3: num("particle_density_kgm3"),
      fluid_density_kgm3: num("fluid_density_kgm3"),
      fluid_viscosity_Pas: num("fluid_viscosity_Pas"),
    };
  }
  if (tool === "cyclone") {
    return {
      gas_flow_m3s: num("gas_flow_m3s"),
      gas_density_kgm3: num("gas_density_kgm3"),
      gas_viscosity_Pas: num("gas_viscosity_Pas"),
      particle_density_kgm3: num("particle_density_kgm3"),
      design: inputs.design || "high_efficiency",
    };
  }
  if (tool === "drag-coefficient") {
    return { Re: num("Re"), sphericity: num("sphericity") ?? 1 };
  }
  if (tool === "humid-air") {
    const out = { T_dry_C: num("T_dry_C"), P_bar: num("P_bar") ?? 1.01325 };
    if (inputs.RH !== undefined && inputs.RH !== "") out.RH = num("RH");
    if (inputs.T_wet_C !== undefined && inputs.T_wet_C !== "") out.T_wet_C = num("T_wet_C");
    if (inputs.T_dew_C !== undefined && inputs.T_dew_C !== "") out.T_dew_C = num("T_dew_C");
    if (inputs.W_kgkg !== undefined && inputs.W_kgkg !== "") out.W_kgkg = num("W_kgkg");
    return out;
  }
  if (tool === "joback") {
    return { smiles: inputs.smiles, T_K: num("T_K") ?? 298.15 };
  }
  if (tool === "mass-balance" || tool === "energy-balance") {
    let streams = {};
    try { streams = JSON.parse(inputs.streams_json || "{}"); } catch {}
    const out = {
      streams,
      feed_streams: csList(inputs.feed_streams),
      product_streams: csList(inputs.product_streams),
      tolerance_pct: num("tolerance_pct") ?? (tool === "mass-balance" ? 1 : 2),
    };
    if (tool === "energy-balance") {
      out.equipment_duties_kW = csList(inputs.equipment_duties_kW).map(Number);
    }
    return out;
  }
  if (tool === "psv") {
    return {
      mass_flow_kgs: num("mass_flow_kgs"),
      P1_Pa: num("P1_Pa"), P2_Pa: num("P2_Pa"),
      T_K: num("T_K"), MW: num("MW"),
      k_ratio: num("k_ratio") ?? 1.3, Z: num("Z") ?? 1,
      service: inputs.service || "vapor",
      density_kgm3: num("density_kgm3"),
      Pc_Pa: num("Pc_Pa"), Psat_Pa: num("Psat_Pa"),
    };
  }
  if (tool === "control-valve") {
    return {
      mass_flow_kgs: num("mass_flow_kgs"),
      P1_Pa: num("P1_Pa"), P2_Pa: num("P2_Pa"),
      density_kgm3: num("density_kgm3"),
      T_K: num("T_K"), service: inputs.service || "liquid",
      Psat_Pa: num("Psat_Pa"), Pc_Pa: num("Pc_Pa"),
      mu_Pas: num("mu_Pas") ?? 1e-3,
      MW: num("MW"), gamma: num("gamma") ?? 1.4,
    };
  }
  if (tool === "coolprop") {
    return { fluid: inputs.fluid || "Water", T_C: num("T_C"), P_bar: num("P_bar") };
  }
  if (tool === "steam") {
    const out = {};
    if (inputs.T_C !== undefined && inputs.T_C !== "") out.T_C = num("T_C");
    if (inputs.P_bar !== undefined && inputs.P_bar !== "") out.P_bar = num("P_bar");
    if (inputs.enthalpy_kJkg !== undefined && inputs.enthalpy_kJkg !== "") out.enthalpy_kJkg = num("enthalpy_kJkg");
    if (inputs.quality !== undefined && inputs.quality !== "") out.quality = num("quality");
    return out;
  }
  if (tool === "utility") {
    return {
      duty_kW: num("duty_kW"),
      utility_type: inputs.utility_type || "cooling_water",
      cw_inlet_T_C: num("cw_inlet_T_C") ?? 30,
      cw_outlet_T_C: num("cw_outlet_T_C") ?? 40,
      hours_per_year: num("hours_per_year") ?? 8000,
    };
  }
  return {};
}
